import { Draft } from 'immer';
import { AppState } from '../interface';
import { OverlayContent } from '../../../enums/elementId';
import { AvcResult, JarvisProduct } from '@vfde-sails/avc-core';

export const setSelectedByID = (draft: Draft<AppState>, newSelectedId: string | undefined) => {
    if (!newSelectedId) {
        return;
    }

    draft.selectedTariff.tariffId = newSelectedId;
    draft.selectedTariff.tariff = draft.tariffs
        .find(tariff => tariff.id === newSelectedId);
};

export const getFastestProduct = (products: JarvisProduct[]): JarvisProduct | undefined =>
    products.reduce((fastest, product) => {
        return +product.download > +fastest.download ? product : fastest;
    }, products[0]);

/**
 * Find and set selected Tariff by selected tariffId
 * If not available find and set fastest available tariff
 *
 * @param draft AppState
 * @param tariffs JarvisProduct[]
 */
const setSelectedTariff = (draft: Draft<AppState>, tariffs: JarvisProduct[]) => {
    if (0 === tariffs.length) {
        return;
    }

    const { tariffId } = draft.selectedTariff;
    const tariff = tariffs.find(t => t.id === tariffId);
    const fastestTariff = getFastestProduct(tariffs);

    if (tariffId && tariff) {
        draft.selectedTariff.tariff = tariff;
    } else if (fastestTariff) {
        draft.selectedTariff.tariff = fastestTariff;
        draft.selectedTariff.tariffId = fastestTariff.id;
    }
};

/**
 * Sets given tariffs based on the currently selected product
 * and sets new selected Tariff is current one is not available
 *
 * @param draft State
 * @param tariffs JarvisProduct[]
 */
export const handleSetTariffs = (
    draft: Draft<AppState>,
    tariffs: JarvisProduct[],
    isInit = false
) => {
    // immer freezes the array so we need a shallow copy first
    const sortedByDownload = [...tariffs].sort((t1, t2) => +t1.download > +t2.download ? -1 : 1);

    draft.tariffs = sortedByDownload;

    if (isInit) {
        draft.initialTariffs = [ ...sortedByDownload ];
    }

    setSelectedTariff(draft, sortedByDownload);
};

/**
 * Sets the avcResult, checked address,
 * and a new selected tariff if the currently selected is not available.
 * If no tariff is available leaves the selection as is.
 *
 * @param draft AppState
 * @param result AvcResult
 */
export const handleSetAvcResult = (draft: Draft<AppState>, result?: AvcResult) => {
    if (!result) {
        draft.address = undefined;
        draft.avcResult = undefined;

        return;
    }

    draft.avcResult = result;
    draft.address = result.address;

    const availableTariffs = draft.tariffs
        .filter(tariff => result.metacodes.includes(tariff.id));

    if (availableTariffs.length) {
        setSelectedTariff(draft, draft.tariffs);
    }

    draft.activeOverlay = OverlayContent.RESULT;
    draft.isLoading = false;
};

export const handleInitStore = (draft: Draft<AppState>, state: Partial<AppState>) => {
    state.isLoading = false;

    state.address && (draft.address = state.address);
    state.selectedTariff && (draft.selectedTariff = state.selectedTariff);
    state.tariffs && (draft.tariffs = state.tariffs);
    state.avcResult && (draft.avcResult = state.avcResult);
};

export const handleResetTariffsToPreAvc = (draft: Draft<AppState>) => {
    draft.tariffs = [ ...draft.initialTariffs ];
    setSelectedTariff(draft, draft.tariffs);
};
